import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch, connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Table,
  message,
  Button,
  Modal,
  Drawer,
  Grid,
  Col,
  Row,
  Space,
  Input,
  Tooltip,
  Alert,
  Select,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { FolderOpen, ListMusic } from "lucide-react";
import moment from "moment";

import MainLayout from "../../components/layouts/main";
import PlaylistTableView from "../../components/views/PlaylistTableView";
import PageLoader from "../../components/PageLoader";
import PrimaryActionButton from "../../components/PrimaryActionButton";
import DropZoneContainer from "../../components/views/DropZoneContainer";
import DroppablePlaylistMedias from "../../components/views/DroppablePlaylistMedias";
import BrandFileListView from "../../components/views/BrandFileListView";
import MediaFilter from "../../components/views/MediaFilter";
import PopupManager from "../../managers/PopupManager";
import PermissionManager from "../../managers/PermissionManager";
import DataHelper from "../../helpers/DataHelper";
import Constants from "../../data/constants";
import Animations from "../../animations/Animations";
import {
  DEFAULT_FFILE_SCALING,
  DEFAULT_MUTED,
  DEFAULT_WIDGET_SCALING,
} from "../../Config";
import useFolderFileStore from "../../store/folderFileStore";
import {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectBranch,
  selectPage,
} from "../../actions";
import { isEqual } from "lodash";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";
import { THEME_PALLETE } from "../../Theme";

const PlaylistPage = (props) => {
  const { playlistid } = useParams();

  return <PlaylistPageFunctional playlistID={playlistid} {...props} />;
};

const PlaylistPageFunctional = (props) => {
  const { lang } = useProLanguage();
  const t = PRO_TRANSLATIONS[lang].playlist;
  const screens = Grid.useBreakpoint();

  const [playlistDatas, setPlaylistDatas] = useState([]);
  const [selectedPlaylistData, setSelectedPlaylistData] = useState(null);
  const [attachedScreens, setAttachedScreens] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchData, setBranchData] = useState(null);
  const [brandData, setBrandData] = useState(null);
  const [mediaSearchWord, setMediaSearchWord] = useState("");
  const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);
  const [playlistMediaRefreshKey, setPlaylistMediaRefreshKey] = useState(0);
  const [currentFilters, setCurrentFilters] = useState({});
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const brandFileListRef = useRef(null);
  const reloadFileDatas = useRef(null);
  const selectedFolderID = useFolderFileStore(
    (state) => state.selectedFolderID,
  );
  const setSelectedFolderID = useFolderFileStore(
    (state) => state.setSelectedFolderID,
  );

  const { playlistid } = useParams();
  const history = useHistory();

  // Detect if we are on the brand-level playlist page (no branch)
  const isBrandLevel = window.location.pathname.includes("/brand_playlist");
  const pageId = isBrandLevel
    ? Constants.BRAND_PLAYLIST_PAGE
    : Constants.PLAYLIST_PAGE;

  // Sidebar re-click: when user clicks Playlist menu while already on this page,
  // go back to the playlist list if a detail view is open.
  const prevSelectedPageRef = useRef(props.selectedPage);
  useEffect(() => {
    const prev = prevSelectedPageRef.current;
    const curr = props.selectedPage;
    if (
      curr?.id === pageId &&
      prev?.id === pageId &&
      prev !== curr &&
      selectedPlaylistData
    ) {
      setSelectedPlaylistData(null);
    }
    prevSelectedPageRef.current = curr;
  }, [props.selectedPage]);

  useEffect(() => {
    const newValue = playlistid
      ? playlistDatas.find((p) => `${p.PlaylistID}` === `${playlistid}`) || null
      : null;

    // if value didn't change, skip state update
    setSelectedPlaylistData((prev) => {
      if (prev === newValue) {
        return prev; // no update
      }
      return newValue;
    });
  }, [playlistid, playlistDatas]);

  useEffect(() => {
    reloadAttachedScreens();
  }, [playlistid, selectedPlaylistData]);

  useEffect(() => {
    reloadAttachedScreens();
  }, []);

  const {
    playlistID,
    globalSearch,
    selectedBrandData,
    selectedBranchData,
    usertoken,
    requestServerPost,
  } = props;

  const reloadAttachedScreens = useCallback(() => {
    if (!selectedPlaylistData || !selectedBrandData) {
      setAttachedScreens((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    // Skip screen lookup when no branch is selected (brand-level)
    if (!selectedBranchData) {
      setAttachedScreens([]);
      return;
    }

    DataHelper.getScreensByPlaylist(
      {
        brandid: selectedBrandData.BrandID,
        branchid: selectedBranchData.BranchID,
        playlistid: selectedPlaylistData.PlaylistID,
      },
      props,
    ).then((data) => {
      setAttachedScreens((prev) => (isEqual(prev, data) ? prev : data));
    });
  }, [selectedPlaylistData, selectedBrandData, selectedBranchData, props]);

  // Subscribe to Zustand store
  useEffect(() => {
    setSelectedFolderID(null);

    const unsubscribe = useFolderFileStore.subscribe(
      (state) => state.selectedFolderID,
      setSelectedFolderID,
    );

    return () => unsubscribe();
  }, []);

  // Load branches for this brand
  useEffect(() => {
    if (!selectedBrandData) return;
    if (
      isBrandLevel &&
      (selectedBrandData.ContentSharingMode === 2 ||
        selectedBrandData.ContentSharingMode === "2")
    ) {
      DataHelper.getBranches(
        { brandid: selectedBrandData.BrandID },
        props,
      ).then((data) => {
        setBranches(data);
      });
    } else {
      setBranches([]);
    }
  }, [selectedBrandData, isBrandLevel]);

  // Reload playlists when relevant props change
  const reloadPage = useCallback(async () => {
    if (!selectedBrandData) return;
    if (!isBrandLevel && !selectedBranchData) return;

    if (!brandData) setBrandData(selectedBrandData);
    if (!isBrandLevel && !branchData) setBranchData(selectedBranchData);

    const params = { brandid: selectedBrandData.BrandID };
    if (!isBrandLevel && selectedBranchData) {
      params.branchid = selectedBranchData.BranchID;
    }

    setPlaylistsLoading(true);
    setLoadError(false);
    try {
      const data = await DataHelper.getPlaylists(params, {
        playlistID,
        ...props,
      });

      const selected = playlistID
        ? data.find((p) => `${p.PlaylistID}` === `${playlistID}`)
        : null;

      setPlaylistDatas(data);
      setSelectedPlaylistData(selected);
    } catch (err) {
      setLoadError(true);
      message.error(t.loadError);
    } finally {
      setPlaylistsLoading(false);
    }
  }, [
    selectedBrandData,
    selectedBranchData,
    playlistID,
    brandData,
    branchData,
    props,
    t,
  ]);

  useEffect(() => {
    reloadPage();
  }, [reloadPage]);

  // Handlers
  const handleCreatePlaylist = () =>
    PopupManager.launchCreatePlaylistPopup(reloadPage);
  const handleEditPlaylist = (playlistData) =>
    PopupManager.launchEditPlaylistPopup(playlistData, reloadPage);
  const handleDuplicatePlaylist = (playlistData) =>
    PopupManager.launchDuplicatePlaylistPopup(playlistData, reloadPage);
  const handleFilesUploaded = () =>
    reloadFileDatas.current && reloadFileDatas.current();

  // Back to the list: clear the selection AND the playlistid in the URL so the
  // last-opened playlist stays selectable (pushing its same URL would be a no-op).
  const handleBackToPlaylists = () => {
    setSelectedPlaylistData(null);
    if (!selectedBrandData) return;
    if (!isBrandLevel && selectedBranchData) {
      history.push(
        `/${selectedBrandData.BrandID}/${selectedBranchData.BranchID}/playlist`,
      );
    } else {
      history.push(`/${selectedBrandData.BrandID}/brand_playlist`);
    }
  };

  const handleQuickAddMediaToPlaylist = useCallback(
    (fileData) => {
      if (!selectedPlaylistData || !selectedBrandData || !fileData) return;

      const fileProps = DataHelper.extractFileProperties(fileData);
      const { duration = 30 } = fileProps;

      const params = {
        playlistid: selectedPlaylistData.PlaylistID,
        brandid: selectedBrandData.BrandID,
        medianame: fileData.FileName,
        startat: 0,
        duration: parseFloat(duration),
        fileid: fileData.FileID,
        scale: DEFAULT_FFILE_SCALING,
        muted: DEFAULT_MUTED,
        startdate: "",
        enddate: "",
      };

      requestServerPost(
        "create_media",
        params,
        {},
        () => {
          message.success(t.addSuccess(fileData.FileName));
          setPlaylistMediaRefreshKey((prev) => prev + 1);
          reloadAttachedScreens();
        },
        usertoken,
        {},
        () => {
          message.error(t.addError(fileData.FileName));
        },
      );
    },
    [
      selectedPlaylistData,
      selectedBrandData,
      requestServerPost,
      usertoken,
      reloadAttachedScreens,
      t,
    ],
  );

  const handleQuickAddWidgetToPlaylist = useCallback(
    (widgetData) => {
      if (!selectedPlaylistData || !selectedBrandData || !widgetData) return;

      const fileProps = DataHelper.extractFileProperties(widgetData);
      const { duration = 30 } = fileProps;

      const params = {
        playlistid: selectedPlaylistData.PlaylistID,
        brandid: selectedBrandData.BrandID,
        medianame: widgetData.WidgetInstanceName,
        mediatype: "Widget",
        startat: 0,
        duration: parseFloat(duration),
        widgetinstanceid: widgetData.WidgetInstanceID,
        scale: DEFAULT_WIDGET_SCALING,
        startdate: "",
        enddate: "",
      };

      requestServerPost(
        "create_media",
        params,
        {},
        () => {
          message.success(t.addSuccess(widgetData.WidgetInstanceName));
          setPlaylistMediaRefreshKey((prev) => prev + 1);
          reloadAttachedScreens();
        },
        usertoken,
        {},
        () => {
          message.error(t.addError(widgetData.WidgetInstanceName));
        },
      );
    },
    [
      selectedPlaylistData,
      selectedBrandData,
      requestServerPost,
      usertoken,
      reloadAttachedScreens,
      t,
    ],
  );

  // Breadcrumb
  const renderBreadcrumb = () => {
    return [
      <span
        onClick={handleBackToPlaylists}
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: "#64748b",
          padding: "4px 8px",
          lineHeight: "22px",
          cursor: "pointer",
        }}
      >
        {t.playlist}
      </span>,
      selectedPlaylistData && [
        "/",
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "#94a3b8",
            padding: "4px 8px",
            lineHeight: "22px",
          }}
        >
          {selectedPlaylistData.PlaylistName}
        </span>,
      ],
    ];
  };

  // Render playlist table
  const renderPlaylistsView = () => {
    const createPermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_PLAYLIST,
      PermissionManager.PERMISSION_TYPE_CREATE,
    );

    const filteredData = playlistDatas.filter((pl) => {
      let match = pl.PlaylistName.toLowerCase().includes(
        globalSearch.toLowerCase(),
      );

      // Branches filter
      if (
        currentFilters.branches &&
        currentFilters.branches.length > 0 &&
        !currentFilters.branches.includes("all")
      ) {
        const isPublic =
          !pl.branches ||
          !Array.isArray(pl.branches) ||
          pl.branches.length === 0;
        if (isPublic) {
          match = match && true;
        } else {
          const hasIntersection = currentFilters.branches.some((b) =>
            pl.branches.includes(Number(b)),
          );
          match = match && hasIntersection;
        }
      }

      // CreateLevel mapping assuming Ownership maps to CreateLevel if it exists
      if (currentFilters.createLevel && currentFilters.createLevel !== "all") {
        match =
          match &&
          String(pl.CreateLevel) === String(currentFilters.createLevel);
      }

      // Date filtering assuming AddDate is available
      if (currentFilters.dateRange && currentFilters.dateRange.length) {
        const [start, end] = currentFilters.dateRange;
        const dateToCheck = pl.AddDate;
        if (dateToCheck) {
          const isWithin = moment(dateToCheck, "YYYY-MM-DD").isBetween(
            moment(start).startOf("day"),
            moment(end).endOf("day"),
            null,
            "[]",
          );
          match = match && isWithin;
        }
      }

      return match;
    });

    return (
      <MainLayout
        renderBreadCrumb={renderBreadcrumb}
        id={pageId}
        style={{ padding: 20 }}
      >
        <motion.div {...Animations.PAGE_ENTER}>
          {/* Page header */}
          <div className="cms-page-header">
            <div className="cms-page-header-info">
              <div
                className="cms-page-header-icon"
                style={{ background: THEME_PALLETE.gradientBrand }}
              >
                <ListMusic size={26} color={THEME_PALLETE.white} />
              </div>
              <div className="cms-page-header-text">
                <h1>{t.title}</h1>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 4,
                    flexWrap: "wrap",
                  }}
                >
                  {selectedBrandData && (
                    <div
                      style={{
                        fontSize: 13,
                        color: THEME_PALLETE.textSecondary,
                      }}
                    >
                      {selectedBrandData.BrandName}
                    </div>
                  )}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px 10px",
                      borderRadius: 999,
                      border: `1px solid ${THEME_PALLETE.primaryBorder}`,
                      background: THEME_PALLETE.gradientPrimarySoft,
                      color: THEME_PALLETE.primaryDark,
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {filteredData.length}
                  </span>
                </div>
              </div>
            </div>
            {createPermission && (
              <PrimaryActionButton
                className="cms-page-header-action"
                onClick={handleCreatePlaylist}
              >
                {t.newPlaylist}
              </PrimaryActionButton>
            )}
          </div>

          <MediaFilter
            mode="playlist"
            onFilterChange={setCurrentFilters}
            branches={branches}
          />

          <div style={{ marginTop: 20 }}>
            {loadError ? (
              <Alert
                type="error"
                showIcon
                message={t.loadError}
                action={
                  <Button size="small" onClick={reloadPage}>
                    {t.retry}
                  </Button>
                }
              />
            ) : (
              <PageLoader loading={playlistsLoading} text={t.loadingPlaylists}>
                <PlaylistTableView
                  data={filteredData}
                  onRowClicked={(playlistData) => {
                    if (!isBrandLevel && selectedBranchData) {
                      history.push(
                        `/${selectedBrandData.BrandID}/${selectedBranchData.BranchID}/playlist/${playlistData.PlaylistID}`,
                      );
                    } else {
                      history.push(
                        `/${selectedBrandData.BrandID}/brand_playlist/${playlistData.PlaylistID}`,
                      );
                    }
                  }}
                  onDelete={(playlistData) => {
                    Modal.confirm({
                      title: t.deleteTitle,
                      content: t.deleteConfirmContent(
                        playlistData.PlaylistName,
                      ),
                      okText: t.deleteOk,
                      cancelText: t.cancel,
                      okButtonProps: { danger: true },
                      centered: true,
                      onOk: () =>
                        new Promise((resolve, reject) => {
                          requestServerPost(
                            "delete_playlist",
                            { playlistid: playlistData.PlaylistID },
                            {},
                            () => {
                              message.success(
                                t.deleteSuccess(playlistData.PlaylistName),
                              );
                              reloadPage();
                              resolve();
                            },
                            usertoken,
                            {},
                            () => {
                              message.error(t.deleteError);
                              reject();
                            },
                          );
                        }),
                    });
                  }}
                  onEdit={handleEditPlaylist}
                  onDuplicate={handleDuplicatePlaylist}
                  branches={branches}
                  selectedBrandData={selectedBrandData}
                  selectedBranchData={selectedBranchData}
                />
              </PageLoader>
            )}
          </div>
        </motion.div>
      </MainLayout>
    );
  };

  // Render edit view
  const renderEditPlaylistView = () => {
    const editPermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_PLAYLIST,
      PermissionManager.PERMISSION_TYPE_EDIT,
    );
    const createMediaPermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_MEDIA,
      PermissionManager.PERMISSION_TYPE_CREATE,
    );
    const isDesktopWide = screens.xxl === true;

    const renderMediaLibraryPanel = (forDrawer = false) => (
      <>
        <div
          style={{
            padding: "18px 16px 14px",
            borderBottom: `1px solid ${THEME_PALLETE.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: THEME_PALLETE.gradientPrimarySoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FolderOpen size={15} color={THEME_PALLETE.primary} />
            </div>
            <span
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: THEME_PALLETE.textHeading,
              }}
            >
              {t.mediaFiles}
            </span>
          </div>
          {createMediaPermission && (
            <Tooltip title={t.uploadMedia}>
              <Button
                shape="circle"
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  PopupManager.launchFileUploader(
                    handleFilesUploaded,
                    selectedFolderID,
                  )
                }
              />
            </Tooltip>
          )}
        </div>
        <div style={{ padding: "12px 16px 0" }}>
          <Input
            value={mediaSearchWord}
            onChange={(e) => setMediaSearchWord(e.target.value)}
            placeholder={t.filterMedia}
          />
        </div>
        <div
          style={{
            maxHeight: forDrawer
              ? "calc(100vh - 250px)"
              : "calc(100vh - 320px)",
            minHeight: 300,
            overflow: "auto",
            marginTop: 8,
          }}
        >
          <BrandFileListView
            reloadFunctionMapper={(reloadFN) => {
              reloadFileDatas.current = reloadFN;
            }}
            ref={brandFileListRef}
            disableDrag={forDrawer}
            filterWord={mediaSearchWord}
            isBrandLevel={isBrandLevel}
            onQuickAddMedia={
              !isDesktopWide ? handleQuickAddMediaToPlaylist : null
            }
            onQuickAddWidget={
              !isDesktopWide ? handleQuickAddWidgetToPlaylist : null
            }
          />
        </div>
      </>
    );

    return (
      <MainLayout
        renderBreadCrumb={renderBreadcrumb}
        id={pageId}
        style={{ padding: 20 }}
        preventDefaultStepBack={true}
        onBackClicked={handleBackToPlaylists}
      >
        <motion.div {...Animations.PAGE_ENTER}>
          {/* Pending publish alert */}
          {attachedScreens.length > 0 &&
            attachedScreens.some((s) => s.PendingPublished === 1) && (
              <div style={{ marginBottom: 12 }}>
                <Alert
                  type="warning"
                  showIcon
                  message={t.pendingPublish(
                    attachedScreens.filter((s) => s.PendingPublished === 1)
                      .length,
                    attachedScreens.length,
                  )}
                />
              </div>
            )}

          {/* Header + media list (left) and media library (right) share one grid
              so the header width comes from the column and both columns align at
              the same top — no magic-number width or negative margin needed. */}
          <Row
            style={{ marginTop: 4 }}
            gutter={isDesktopWide ? [12, 0] : [0, 12]}
          >
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              xxl={editPermission ? 19 : 24}
              style={{ padding: 0 }}
            >
              {/* Detail page header */}
              <div
                className="cms-page-header"
                style={{
                  marginBottom: screens.xxl && editPermission ? 14 : 20,
                }}
              >
                <div className="cms-page-header-info">
                  <div
                    className="cms-page-header-icon"
                    style={{ background: THEME_PALLETE.gradientBrand }}
                  >
                    <ListMusic size={26} color={THEME_PALLETE.white} />
                  </div>
                  <div className="cms-page-header-text">
                    <h1
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedPlaylistData.PlaylistName}
                    </h1>
                    {selectedBrandData && <p>{selectedBrandData.BrandName}</p>}
                  </div>
                </div>
                {editPermission && !isDesktopWide && (
                  <PrimaryActionButton
                    className="cms-page-header-action"
                    icon={<FolderOpen size={15} />}
                    onClick={() => setLibraryDrawerOpen(true)}
                  >
                    {t.addMediaFiles}
                  </PrimaryActionButton>
                )}
              </div>

              {/* Media list */}
              <div
                style={{
                  background: THEME_PALLETE.bgPage,
                  borderRadius: 16,
                  padding: "12px 12px 4px",
                }}
              >
                <DropZoneContainer groupName="file">
                  <DroppablePlaylistMedias
                    key={`${selectedPlaylistData.PlaylistID}-${playlistMediaRefreshKey}`}
                    allowEdit={editPermission}
                    allowDelete={editPermission}
                    playlistID={selectedPlaylistData.PlaylistID}
                    needsPublishCount={
                      attachedScreens.filter((s) => s.PendingPublished === 1)
                        .length
                    }
                    onAssignPublishCallback={() => {
                      reloadAttachedScreens();
                    }}
                    onPlaylistDataUpdated={() => {
                      reloadAttachedScreens();
                    }}
                  />
                </DropZoneContainer>
              </div>
            </Col>
            {editPermission && isDesktopWide && (
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={5}
                style={{
                  backgroundColor: THEME_PALLETE.bgCard,
                  border: `1px solid ${THEME_PALLETE.border}`,
                  borderRadius: 16,
                  overflow: "hidden",
                }}
              >
                {renderMediaLibraryPanel(false)}
              </Col>
            )}
          </Row>

          {editPermission && !isDesktopWide && (
            <Drawer
              title={t.mediaFiles}
              placement="bottom"
              height="78vh"
              onClose={() => setLibraryDrawerOpen(false)}
              open={libraryDrawerOpen}
              destroyOnClose={false}
              bodyStyle={{ padding: 0 }}
            >
              <div
                style={{
                  background: THEME_PALLETE.bgCard,
                  height: "100%",
                }}
              >
                {renderMediaLibraryPanel(true)}
              </div>
            </Drawer>
          )}
        </motion.div>
      </MainLayout>
    );
  };

  return selectedPlaylistData
    ? renderEditPlaylistView()
    : renderPlaylistsView();
};

const mapStateToProps = (state) => ({
  userdata: state.userdata,
  usertoken: state.usertoken,
  globalSearch: state.globalSearch,
  selectedBrandData: state.selectedBrandData,
  selectedBranchData: state.selectedBranchData,
  selectedPage: state.selectedPage,
});

export default connect(mapStateToProps, {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectBranch,
  selectPage,
})(PlaylistPageFunctional);
